import { useState } from 'react'

import {
  useZoneSetPriorityMutation,
  ZonesListDocument,
} from '~/gql/generated/graphql'

type useBulkZonePriorityMutationProps = {
  onCompleted: (failedIds: string[]) => void
  onError: (err: unknown) => void
}

type UpdateFunctionProps = {
  zoneIds: string[]
  isPriority: boolean
}

export function useBulkZonePriorityMutation({
  onCompleted,
  onError,
}: useBulkZonePriorityMutationProps): [
  (UpdateFunctionProps) => Promise<string[]>,
  boolean,
] {
  const [loading, setLoading] = useState(false)

  const [setPriority] = useZoneSetPriorityMutation({ onError })

  const updateFunction = async ({
    zoneIds,
    isPriority,
  }: UpdateFunctionProps) => {
    if (zoneIds.length !== 0) {
      let failedZoneIds: string[] = []
      setLoading(true)

      zoneIds.forEach(async (id, index) => {
        const isLastZone = index + 1 === zoneIds.length

        const refetchQueries = isLastZone ? [ZonesListDocument] : undefined

        let result: Awaited<ReturnType<typeof setPriority>>

        try {
          result = await setPriority({
            variables: { id, is_priority: isPriority },
            refetchQueries,
          })
        } finally {
          // Whether it worked or not, set loading false when it gets to the last zone
          if (isLastZone) {
            setLoading(false)
            onCompleted(failedZoneIds)
          }
        }
        if (!result || result?.errors) {
          failedZoneIds = [...failedZoneIds, id]
        }
      })

      return failedZoneIds
    }
    return []
  }

  return [updateFunction, loading]
}
